import React, { useState, useEffect } from "react";
import {
  Modal,
  Flex,
  Button,
  LinkButton,
  LoadingSpinner,
  Box,
} from "@catchoftheday/cg-components";
import { useEventBus, legacyEventBus } from "@catchoftheday/state";
import queryString from "query-string";
import { withApollo } from "@catchoftheday/graphql-utils";
import { LinkedContent } from "./LinkedContent";
import { UnlinkedContent } from "./UnlinkedContent";
import {
  TRIGGER_ONEPASS_LINKING_MODAL_EVENT,
  OnePassLinkingModalEventPayloadProps,
} from ".";
import { generateOnePassLinkingUrl } from "@common-vendors/utilities";
import { ONEPASS_LINK_QUERY_PARAM_KEY } from "@common-vendors/consts";
import { useCustomerQuery } from "@common-vendors/generated/graphql";

export const OnePassLinkingModal = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [originalUrl, setOriginalUrl] = useState("");
  const [originalQueryParams, setOriginalQueryParams] = useState<{
    [key: string]: string;
  }>({});

  const { data, loading } = useCustomerQuery({ ssr: false });
  const { customer } = data || {};
  const { membership } = customer || {};

  const eventHandler = (payload: OnePassLinkingModalEventPayloadProps) => {
    setOriginalUrl(payload?.originalUrl || "");
    setIsOpen(true);
  };
  useEventBus<OnePassLinkingModalEventPayloadProps>(
    TRIGGER_ONEPASS_LINKING_MODAL_EVENT,
    (event) => {
      if (event.payload) {
        eventHandler(event.payload);
      }
    }
  );
  useEffect(() => {
    if (typeof window !== "undefined") {
      const queryParams = queryString.parse(window?.location?.search);
      if (queryParams?.[ONEPASS_LINK_QUERY_PARAM_KEY] === "1") {
        delete queryParams?.[ONEPASS_LINK_QUERY_PARAM_KEY];
        setOriginalQueryParams(queryParams);
        setOriginalUrl(window?.location?.pathname);
        setIsOpen(true);
      }
    }

    legacyEventBus?.on(TRIGGER_ONEPASS_LINKING_MODAL_EVENT.type, eventHandler);
    return () => {
      legacyEventBus?.detach(
        TRIGGER_ONEPASS_LINKING_MODAL_EVENT.type,
        eventHandler
      );
    };
  }, []);

  const proceedUrl = generateOnePassLinkingUrl(
    originalUrl,
    originalQueryParams
  );

  return (
    <Modal
      isOpen={isOpen}
      zIndex={101}
      maxWidth="600px"
      width={{ sm: "90%", md: "50%" }}
      height={{ sm: "auto" }}
      borderRadius="16px"
      closeButtonProps={{
        bg: "onePass.primaryDarker",
        width: "36px",
        height: "36px",
      }}
      closeIconProps={{ width: "16px", height: "16px" }}
      onRequestClose={() => setIsOpen(false)}
    >
      {loading && (
        <LoadingSpinner
          containerProps={{ "aria-label": "loading spinner", zIndex: 1 }}
          hasOverlay
          ringBg="ui.borderColor"
        />
      )}
      <Box visibility={loading ? "hidden" : "visible"}>
        {membership?.linked ? (
          <LinkedContent email={customer?.email} />
        ) : (
          <UnlinkedContent email={customer?.email} />
        )}
        <Flex flexDirection={{ xs: "column", md: "row" }}>
          <LinkButton
            href={proceedUrl}
            variant="onePassPrimary"
            flexBasis="50%"
            sizeVariant="md"
            textTransform="uppercase"
            fontFamily="onePass"
            fontWeight="semibold"
          >
            Proceed
          </LinkButton>
          <Button
            variant="plain"
            flexBasis="50%"
            sizeVariant="md"
            onClick={() => setIsOpen(false)}
            color="onePass.primary"
            textTransform="uppercase"
            fontFamily="onePass"
            fontWeight="semibold"
            underlineOnHover
          >
            Cancel
          </Button>
        </Flex>
      </Box>
    </Modal>
  );
};

export const OnePassLinkingModalWithApollo = withApollo({
  uri: process.env.NEXT_PUBLIC_GRAPHQL_ENDPOINT as string,
})(OnePassLinkingModal);
